import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { loadBlogPost, loadBlogPosts, markdownToHtml } from '../utils/content';
import FontAwesomeIcon from '../components/FontAwesomeIcon';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadContent() {
      try {
        const blogPost = await loadBlogPost(slug);

        if (!blogPost) {
          navigate('/blog');
          return;
        }

        setPost(blogPost);

        // Load related posts (same category)
        if (blogPost.categories && blogPost.categories.length > 0) {
          const allPosts = await loadBlogPosts();
          const related = allPosts
            .filter(
              p =>
                p.slug !== slug &&
                p.categories &&
                p.categories.some(cat => blogPost.categories.includes(cat))
            )
            .slice(0, 3);
          setRelatedPosts(related);
        }
      } catch (error) {
        console.error('Error loading blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    }
    loadContent();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon
            icon="fa-spinner"
            className="w-12 h-12 text-safari-gold animate-spin mx-auto mb-4"
          />
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-safari-light py-4 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-safari-gold transition">
              Home
            </Link>
            <FontAwesomeIcon icon="fa-chevron-right" className="w-3 h-3" />
            <Link to="/blog" className="hover:text-safari-gold transition">
              Blog
            </Link>
            <FontAwesomeIcon icon="fa-chevron-right" className="w-3 h-3" />
            <span className="text-gray-900 font-semibold truncate">{post.title}</span>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Categories */}
            {post.categories && post.categories.length > 0 && (
              <div className="flex gap-2 mb-6 flex-wrap">
                {post.categories.map((cat, idx) => (
                  <span
                    key={idx}
                    className="text-sm bg-safari-green text-white px-3 py-1 rounded-full"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-heading leading-tight">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-xl text-gray-600 mb-8 leading-relaxed border-l-4 border-safari-gold pl-6 italic">
                {post.excerpt}
              </p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="fa-user" className="w-5 h-5 text-safari-gold" />
                <span className="font-semibold">{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon="fa-calendar"
                  className="w-5 h-5 text-safari-gold"
                />
                <span>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>

            {/* Featured Image */}
            {post.featuredImage && (
              <div className="mb-12 rounded-xl overflow-hidden shadow-lg">
                <img
                  src={post.featuredImage}
                  alt={post.featuredImageAlt || post.title}
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Article Content */}
            <div
              className="prose prose-lg max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(post.body) }}
              style={{
                lineHeight: '1.8',
              }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mb-12 pb-8 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tags:</h3>
                <div className="flex gap-2 flex-wrap">
                  {post.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full hover:bg-safari-gold hover:text-white transition cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mb-12 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Share this article:
              </h3>
              <div className="flex gap-4">
                <a
                  href={`https://facebook.com/sharer/sharer.php?u=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#1877f2] text-white flex items-center justify-center hover:opacity-80 transition"
                  aria-label="Share on Facebook"
                >
                  <FontAwesomeIcon icon="fa-facebook" className="w-5 h-5" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${window.location.href}&text=${post.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:opacity-80 transition"
                  aria-label="Share on Twitter"
                >
                  <FontAwesomeIcon icon="fa-x-twitter" className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#0077b5] text-white flex items-center justify-center hover:opacity-80 transition"
                  aria-label="Share on LinkedIn"
                >
                  <FontAwesomeIcon icon="fa-linkedin" className="w-5 h-5" />
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }}
                  className="w-10 h-10 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-300 transition"
                  aria-label="Copy link"
                >
                  <FontAwesomeIcon icon="fa-link" className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-safari-light p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-safari-green mb-4">
                Ready to Experience Uganda?
              </h3>
              <p className="text-gray-700 mb-6">
                Let us create your perfect safari adventure
              </p>
              <Link to="/contact" className="btn-primary inline-block">
                Start Planning Your Trip
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 font-heading text-safari-green">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map(relatedPost => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  >
                    {relatedPost.featuredImage && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={relatedPost.featuredImage}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      {relatedPost.excerpt && (
                        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                          {relatedPost.excerpt}
                        </p>
                      )}
                      <div className="flex items-center text-safari-gold font-semibold text-sm">
                        Read More
                        <FontAwesomeIcon icon="fa-arrow-right" className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
